import Image from 'next/image';
import Link from 'next/link';
import type { StaticImageBlock } from '../types';
import { toStaticLinkTargetProps } from '../utils/staticLinkTarget';

type StaticImageProps = {
  image: StaticImageBlock;
  'data-testid'?: string;
};

/**
 * Responsive image sourced from static page JSON.
 *
 * Renders through `next/image`. Remote hosts must be allowed in the app's
 * `images.remotePatterns`. `width` and `height` are the intrinsic size, used for
 * the aspect ratio; the image scales to the content width. `loading` passes
 * through, so above-the-fold images can opt out of lazy loading. With `href`,
 * the image is wrapped in a link: internal hrefs stay in the current tab,
 * anything else opens in a new tab.
 *
 * @param image - Source, alt text, intrinsic size and optional link from an `{ type: 'image' }` block
 */
function StaticImage({ image, 'data-testid': dataTestId = 'static-image' }: StaticImageProps) {
  const picture = (
    <Image
      src={image.src}
      alt={image.alt}
      width={image.width}
      height={image.height}
      loading={image.loading}
      sizes="(min-width: 728px) 728px, 100vw"
      className="h-auto w-full rounded-md"
      data-testid={dataTestId}
    />
  );

  if (!image.href) {
    return picture;
  }

  return (
    <Link href={image.href} className="block rounded-md" data-testid={`${dataTestId}-link`} {...toStaticLinkTargetProps(image.href)}>
      {picture}
    </Link>
  );
}

export { StaticImage, type StaticImageProps };
